import { useRef, useState, useEffect } from 'react'
import { Pencil } from 'lucide-react'

interface InlineEditTitleProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  /** A name is mandatory: start empty titles in edit mode and highlight them. */
  required?: boolean
  /** Validation message to show under the title; focuses the input when it appears. */
  error?: string
}

/**
 * An h1 that becomes an editable input on click.
 * Commits on blur or Enter; reverts on Escape.
 */
export function InlineEditTitle({
  value,
  onChange,
  placeholder,
  disabled,
  required,
  error,
}: InlineEditTitleProps) {
  // A required title that is still empty (a new object) opens straight for typing.
  const [editing, setEditing] = useState(() => !!required && !value.trim() && !disabled)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) {
      setDraft(value)
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing, value])

  // A failed save sends the user back to the field that blocked it.
  useEffect(() => {
    if (error && !disabled) setEditing(true)
  }, [error, disabled])

  const missing = !!required && !value.trim()
  const invalid = missing || !!error

  function commit() {
    const trimmed = draft.trim()
    // Only a required title may be cleared — elsewhere an empty commit reverts.
    if (trimmed !== value && (trimmed || required)) onChange(trimmed)
    setEditing(false)
  }

  function cancel() {
    setDraft(value)
    setEditing(false)
  }

  return (
    <span className="flex flex-col gap-1">
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            if (e.key === 'Escape') { e.preventDefault(); cancel() }
          }}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          className={`bg-transparent border-none outline-none text-[length:inherit] font-[inherit] leading-[inherit] w-full min-w-40 border-b-2 ${
            invalid && !draft.trim()
              ? 'border-solid border-b-[color:var(--ad-danger)] placeholder:text-[color:var(--ad-danger)]'
              : ''
          }`}
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setEditing(true)}
          title="Click to edit"
          className="group flex items-center gap-2 bg-transparent border-none p-0 text-left cursor-pointer text-[length:inherit] font-[inherit] leading-[inherit] text-[color:var(--ad-text)] disabled:cursor-default"
        >
          <span>
            {value || (
              <span
                className={`font-normal ${invalid ? 'text-[color:var(--ad-danger)]' : 'text-[color:var(--ad-text-muted)]'}`}
              >
                {placeholder}
              </span>
            )}
          </span>
          {!disabled && (
            <Pencil className="size-3.5 opacity-0 group-hover:opacity-50 transition-opacity shrink-0" />
          )}
        </button>
      )}
      {error && (
        <span className="text-sm font-normal text-[color:var(--ad-danger)]">{error}</span>
      )}
    </span>
  )
}
